import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { useLocationContext } from '@providers/location'
import { isoTodayTZ } from 'components-ui/src/shared/dates'
import { formatUnusedVouchers } from 'components-ui/src/shared/vouchers'
import { useClientReports } from 'service-api/src/hooks/use_client_reports'

const WALLET_BALANCE_REPORT = 'wallet-credit-balance-by-date'
const UNUSED_VOUCHERS_REPORT = 'unused-voucher-offer-count-by-date-v2'

type TUseVouchersBalanceOptions = {
  fetchWalletBalance?: boolean
  fetchUnusedVouchersBalance?: boolean
}

type TUseVouchersBalanceProps = {
  cacheEnabled?: boolean
  options?: TUseVouchersBalanceOptions
}

export const useVouchersBalance = (lupId: string, props?: TUseVouchersBalanceProps) => {
  const { accountName: locationName = '' } = useParams()
  const { cacheEnabled = false, options: { fetchWalletBalance = false, fetchUnusedVouchersBalance = true } = {} } =
    props || {}
  const location = useLocationContext()
  const today = isoTodayTZ(location.location?.timezone ?? undefined)

  const { data: walletBalance, isFetching: fetchingWalletBalance } = useClientReports(
    locationName,
    lupId,
    WALLET_BALANCE_REPORT,
    { filter: { date: today } },
    { cacheEnabled, enabled: !!lupId && fetchWalletBalance }
  )

  const { data: unusedVouchers, isFetching: fetchingUnusedVouchers } = useClientReports(
    locationName,
    lupId,
    UNUSED_VOUCHERS_REPORT,
    { filter: { date: today } },
    { cacheEnabled, enabled: !!lupId && fetchUnusedVouchersBalance }
  )

  const groupedByLSTAndVoucher = useMemo(() => formatUnusedVouchers(unusedVouchers), [unusedVouchers])

  return {
    walletBalance: walletBalance?.total || 0,
    unusedVouchers: {
      total: unusedVouchers?.total || 0,
      items: Object.values(groupedByLSTAndVoucher).sort((a, b) => b.value - a.value),
    },
    isFetching: fetchingWalletBalance || fetchingUnusedVouchers,
    fetchingWalletBalance,
    fetchingUnusedVouchers,
  }
}
